import { useState, type FormEvent } from "react";
const icons = [
  <svg key="i1" className="schematic" viewBox="0 0 96 96" aria-hidden="true">
    <rect x="22" y="34" width="38" height="38" />
    <polyline points="22,34 36,22 74,22 60,34" />
    <polyline points="74,22 74,60 60,72" />
    <line x1="22" y1="82" x2="60" y2="82" />
    <line x1="22" y1="78" x2="22" y2="86" />
    <line x1="60" y1="78" x2="60" y2="86" />
  </svg>,
  <svg key="i2" className="schematic" viewBox="0 0 96 96" aria-hidden="true">
    <rect x="26" y="18" width="44" height="60" />
    <line x1="34" y1="30" x2="62" y2="30" />
    <line x1="34" y1="38" x2="62" y2="38" />
    <line x1="34" y1="46" x2="54" y2="46" />
    <rect x="34" y="56" width="10" height="10" />
    <polyline points="36,61 39,64 44,57" />
  </svg>,
  <svg key="i3" className="schematic" viewBox="0 0 96 96" aria-hidden="true">
    <polyline points="30,18 66,18 66,74 60,70 54,74 48,70 42,74 36,70 30,74 30,18" />
    <line x1="36" y1="30" x2="60" y2="30" />
    <line x1="36" y1="38" x2="60" y2="38" />
    <rect x="36" y="52" width="24" height="9" fill="#FF4400" stroke="none" />
  </svg>,
];
const steps = [
  [
    "01",
    "Your products become AI-readable",
    "AI can understand what you sell, what is in stock, what it costs, and what a buyer actually receives.",
  ],
  [
    "02",
    "AI builds the right order",
    "The right product, variant, price, delivery option, and policy travel together - without costly mistakes.",
  ],
  [
    "03",
    "Your store receives the sale",
    "AI passes a real order into your existing commerce stack. You keep the customer, the order, and the brand.",
  ],
];
const benefits = [
  [
    "More orders",
    "Open a new way for qualified shoppers to buy without adding another ad campaign.",
  ],
  [
    "Less paid-media pressure",
    'Give buyers a direct path from "I need this" to your store - before another click gets auctioned.',
  ],
  [
    "Fewer competitor takeaways",
    "Be ready when AI is choosing where a buyer can actually complete the purchase.",
  ],
];
const faqs = [
  [
    "What does the Priority Product Review provide?",
    "It provides a first, evidence-backed finding about the public product facts that make the submitted SKU harder to compare or trust, plus the priority next step.",
  ],
  [
    "Do you need code access or admin access?",
    "No for the initial review. We start with the public product experience. Any implementation scope is agreed separately, while your customer data, fulfillment, and policies stay yours.",
  ],
  [
    "What happens after I submit a product URL?",
    "We validate the store, review the priority product, and send the first finding. If it needs work, we explain the gap and agree the right next step directly with you.",
  ],
  [
    "What do I receive after the review?",
    "A clear work queue: the product facts that need attention, where they conflict or are missing, and what is worth fixing first. Findings are specific to the submitted store and product.",
  ],
  [
    "Can Foundtro fix what the review finds?",
    "Yes. When the store is a fit, we can scope work across product data, variants, policy facts, feeds, structured data, and storefront consistency, then verify the agreed changes.",
  ],
  [
    "Can you monitor the product facts after they are fixed?",
    "Yes. Ongoing operations can catch catalog, feed, policy, price, availability, and product-signal drift before it quietly affects discovery.",
  ],
  [
    "Do you store customer or card data?",
    "Foundtro does not need customer or card data for the initial review. The request form stores operational details such as the product URL, store domain, request ID, timestamp, IP, and user agent so we can respond and prevent abuse.",
  ],
  [
    "Do you promise rankings or sales?",
    "No. Foundtro reports observed evidence and repairs agreed product facts. Shopping surfaces, assistant behavior, and buyer decisions can change, so we do not promise rankings, recommendations, traffic, or sales.",
  ],
];
function Logo() {
  return (
    <a className="logo logo-image" href="#top" aria-label="Foundtro home">
      <img
        src="/foundtro-logo-product-discovery.svg"
        alt="Foundtro - product discovery operations"
      />
    </a>
  );
}
function RunDoc() {
  return (
    <div
      className="doc"
      aria-label="An example Foundtro product record"
    >
      <div className="dhead mono">
        <span>FOUNDTRO - PRODUCT RECORD No. 0041</span>
        <span>DISCOVERY OPERATIONS</span>
      </div>
      <div className="dbody">
        <div className="drow mono">
          <span>product facts complete</span>
          <b>PASS</b>
        </div>
        <div className="drow mono">
          <span>variants consistently mapped</span>
          <b>PASS</b>
        </div>
        <div className="drow mono off">
          <span>shipping & returns facts</span>
          <s>MISSING</s>
        </div>
        <div className="drow mono off">
          <span>feed and storefront match</span>
          <s>CONFLICT</s>
        </div>
        <div className="drow mono off">
          <span>+ 9 more product signals</span>
          <s>REVIEW</s>
        </div>
        <div className="verdict">
          <span className="mono">PRIORITY PRODUCT</span>
          <strong className="serif">
            REVIEW<i> QUEUE</i>
          </strong>
        </div>
        <div className="order-line mono">
          <span>
            <u />
            EXAMPLE - FACTS TO VERIFY
          </span>
          <span>#4137</span>
        </div>
      </div>
    </div>
  );
}
function Form() {
  const [state, setState] = useState<
    "idle" | "creating" | "received" | "error"
  >("idle");
  const [req, setReq] = useState<any>(null);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setState("creating");
    try {
      const r = await fetch("/api/test-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product: fd.get("product"),
          source: "foundtro.com/#start",
        }),
      });
      const data = await r.json();
      if (!r.ok || !data.request_id)
        throw new Error(data.error || "request failed");
      setReq(data);
      setState("received");
      window.dispatchEvent(new CustomEvent("foundtro:test-request-created", { detail: { request_id: data.request_id, store_domain: data.store_domain } }));
      window.Intercom?.("trackEvent", "foundtro_test_request_created", {
        request_id: data.request_id,
        store_domain: data.store_domain,
      });
    } catch {
      setState("error");
    }
  }
  return (
    <form className="lead premium-test" onSubmit={submit}>
      <div className="test-eyebrow mono">Priority Product Review</div>
      <h3 className="serif">Find the product facts costing you discovery.</h3>
      <p className="test-copy">
        Share one product that matters commercially. We'll register the request,
        review its public product facts, and use the same request ID when the
        priority finding is ready.
      </p>
      <label>
        Product URL
        <input
          required
          name="product"
          type="url"
          placeholder="https://store.com/products/bestseller"
        />
      </label>
      <button disabled={state === "creating"}>
        {state === "creating"
          ? "Creating request..."
          : "Request priority review ->"}
      </button>
      <p className="test-note">
        You will get a request ID immediately. Confirm on WhatsApp so the
        priority finding can be sent to the right thread.
      </p>
      <p className="scope-note">
        We begin with the public product experience. Feed, platform, and
        implementation access are discussed only when needed.
      </p>
      <div className="trustrow">
        <span>No card</span>
        <span>Start with one SKU</span>
        <span>Any commerce platform</span>
      </div>
      {state === "received" && req && (
        <div className="request-panel confirmed">
          <span className="mono">Request {req.request_id}</span>
          <strong>Request received</strong>
          <p>
            Your product URL is saved. Open WhatsApp to confirm the request and
            keep the priority finding in one thread.
          </p>
          {req.whatsapp_url && (
            <a
              className="whatsapp-confirm"
              href={req.whatsapp_url}
              target="_blank"
              rel="noreferrer"
            >
              Confirm on WhatsApp
            </a>
          )}
          <a className="report-link" href={`/report/${req.request_id}`}>
            View report page
          </a>
          <small>Status: {req.status || "request_received"}</small>
        </div>
      )}
      {state === "error" && (
        <p className="ferr">
          Could not create the request. Try again or email hannah@foundtro.com.
        </p>
      )}
    </form>
  );
}
export default function App() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <header>
        <div className="nav">
          <Logo />
          <p className="mono navtag">
              PRODUCT FACTS
              <br />
              MADE READY TO COMPARE
          </p>
          <button className="menu mono" onClick={() => setOpen(!open)}>
            {open ? "CLOSE" : "MENU"}
          </button>
          <nav className={open ? "open" : ""}>
            <a href="#how" className="mono">
              WHAT WE FIX
            </a>
            <a href="/product-discovery-operations" className="mono">
              OUR METHOD
            </a>
            <a href="#evidence" className="mono">
              EVIDENCE
            </a>
            <a href="#faq" className="mono">
              FAQ
            </a>

            <a href="/contact" className="mono">
              CONTACT
            </a>
          </nav>
          <a className="nav-cta mono" href="#start">
            REVIEW A PRODUCT
          </a>
        </div>
      </header>
      <main id="top">
        <section className="hero">
          <div className="copy">
            <h1 className="serif">
              Fix the facts that
              <br />
              cost your products <em>discovery</em><span className="o">.</span>
            </h1>
            <p className="dek">
              Foundtro makes <b>your priority products easier to understand, compare, and choose</b> across your store, feeds, and shopping surfaces.{" "}
              <b>Clear findings. Practical fixes. Verification when the work is done.</b>
            </p>
            <div className="hero-ctas">
              <a className="a mono" href="#start">
                REVIEW MY PRIORITY PRODUCT
              </a>
              <a className="b mono" href="/product-discovery-operations">
                SEE A SAMPLE REVIEW {"->"}
              </a>
            </div>
            <p className="foot-fact mono">
              START WITH ONE SKU - PUBLIC PRODUCT REVIEW - NO CARD
            </p>
          </div>
          <aside className="hero-doc">
            <figure className="photo hero-photo">
              <img
                src="/press-hero.jpg"
                alt="A Foundtro product-discovery concept illustration in front of a store product wall"
              />
            </figure>
            <p className="stamp mono">
              PRODUCT FACTS, REVIEWED BEFORE BUYERS COMPARE
            </p>
          </aside>
        </section>{" "}
        <div className="strip mono">
          <span>PRIORITY SKUS</span>
          <i>/</i>
          <span>CONSISTENT FACTS</span>
          <i>/</i>
          <span>FEWER DISCOVERY GAPS</span>
        </div>
        <section className="journey" id="how">
          <p className="label mono">
            01 - HOW IT WORKS - PRODUCT URL TO PRIORITY WORK QUEUE
          </p>
          <div className="jphotos">
            <figure className="photo jshot">
              <img
                src="/press-step1.jpg"
                alt="A product page being reviewed against a checklist"
                loading="lazy"
              />
              <figcaption className="mono">01 - PICK A PRIORITY PRODUCT</figcaption>
            </figure>
            <figure className="photo jshot">
              <img
                src="/press-step2.jpg"
                alt="Three chairs compared for product facts"
                loading="lazy"
              />
              <figcaption className="mono">02 - REVIEW THE PRODUCT FACTS</figcaption>
            </figure>
            <figure className="photo jshot">
              <img
                src="/press-step3.jpg"
                alt="A product record being prepared for review"
                loading="lazy"
              />
              <figcaption className="mono">03 - FIND THE CONFLICTS</figcaption>
            </figure>
            <figure className="photo jshot">
              <img
                src="/press-step4.jpg"
                alt="A sealed shipping box labeled fulfilled"
                loading="lazy"
              />
              <figcaption className="mono">
                04 - FIX WHAT MATTERS FIRST
              </figcaption>
            </figure>
          </div>
          <p className="routeline mono">
            PRODUCT URL <span>{"->"}</span> REQUEST ID <span>{"->"}</span> PRODUCT
            REVIEW <span>{"->"}</span> <b>PRIORITY FIXES</b>{" "}
            <span>- IMPLEMENTATION SCOPE AGREED SEPARATELY</span>
          </p>
          <a className="jcta mono" href="#start">
            CREATE THE REQUEST {"->"}
          </a>
        </section>
        <section className="evidence" id="evidence">
          <div className="ev-copy">
            <p className="label mono">02 - EXAMPLE PRODUCT RECORD</p>
            <h2 className="serif">
              A score is not a plan. <em>Fix the product fact.</em>
            </h2>
            <p>
              The useful output is not a generic grade. It is a short, specific
              work queue for the product facts that need repair first.
            </p>
            <p>
              <b>Example finding:</b> the selected variant and delivery facts
              disagree between the product page and feed.
            </p>
            <a className="tlink mono" href="/product-discovery-operations">
              SEE A SAMPLE PRODUCT RECORD {"->"}
            </a>
          </div>
          <aside className="ev-doc">
            <RunDoc />
            <div className="stamp-mark mono">
              EXAMPLE
              <br />
              PRODUCT RECORD
            </div>
          </aside>
        </section>
        <section className="trust">
          <p className="label mono">
            03 - WHAT STAYS YOURS - BUILT FOR YOUR COMMERCE STACK
          </p>
          <div className="trust-grid">
            <div>
              <h2 className="serif">
                Your stack stays yours. <em>Your priority products get the attention.</em>
              </h2>
              <figure className="photo">
                <img
                  src="/press-grip.jpg"
                  alt="A product record filed with an orange Foundtro tag"
                  loading="lazy"
                />
                <figcaption className="mono">
                  FIG. 03 - THE PRODUCT RECORD, FILED. READY TO COMPARE.
                </figcaption>
              </figure>
            </div>
            <ul className="mono">
              <li>
                CLEAR SCOPE BEFORE <b>ANY BUILD</b>
              </li>
              <li>
                PRIORITY FIXES, NOT <b>BUSYWORK</b>
              </li>
              <li>
                YOUR STACK STAYS <b>YOURS</b>
              </li>
              <li>
                AGREED CHANGES <b>VERIFIED</b>
              </li>
              <li>
                DELAWARE, UNITED STATES <b>REGISTERED BUSINESS</b>
              </li>
            </ul>
          </div>
        </section>
        <section className="faq" id="faq">
          <div className="faq-head">
            <p className="label mono">FAQ - OPERATING NOTES</p>
            <h2 className="serif">Answers a commerce team can act on.</h2>
            <p>
              Plain operational answers for ecommerce teams deciding whether
              Foundtro is the right next step.
            </p>
          </div>
          <div className="faq-grid">
            {faqs.map(([q, a], i) => (
              <details className="faq-item" key={q} open={i < 2}>
                <summary>
                  <span className="mono">{String(i + 1).padStart(2, "0")}</span>
                  <strong>{q}</strong>
                </summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </section>
        <figure className="band">
          <img
            src="/press-receipts.jpg"
            alt="A pile of product records with an orange Foundtro tag"
            loading="lazy"
          />
        </figure>
        <section className="start" id="start">
          <div className="start-copy">
            <p className="label mono">05 - START HERE</p>
            <h2 className="serif">
              Which product facts need attention <em>first?</em>
            </h2>
            <p>
              Drop the link to a product that matters commercially. We start by
              checking the public facts shoppers and shopping systems need to
              compare it with confidence.
            </p>
            <p className="fine">
              One priority product review per store. You see the first evidence
              before discussing any paid implementation.
            </p>
            <figure className="photo start-photo">
              <img
                src="/press-hand.jpg"
                alt="A robotic hand holding a Foundtro shopping bag"
                loading="lazy"
              />
            </figure>
          </div>
          <Form />
        </section>
      </main>
      <footer>
        <Logo />
        <p>Foundtro keeps important ecommerce products ready to be found, compared, and understood.</p>
        <div className="mono">
          <a href="/privacy">PRIVACY</a>
          <a href="/terms">TERMS</a>
          <a href="/refund">REFUND</a>

          <a href="/contact">CONTACT</a>
        </div>
      </footer>
    </>
  );
}




